import Dexie, { type Table } from 'dexie';
import { useSyncExternalStore } from 'react';

import type {
    AnswerRecord,
    BatchRecord,
    ConflictRecord,
    FindingRecord,
    InspectionRecord,
    MultiCountRecord,
    OrderRecord,
    OutboxOperation,
    PackageDocumentRecord,
    PhotoRecord,
    SyncMetaRecord,
    TemplateVersionRecord,
} from './records';

/**
 * Opslag per omgeving
 * ===================
 *
 * Een keurmeester werkt soms voor meerdere keuringsbedrijven op één tablet.
 * Dossiers van het ene bedrijf mogen nooit in het andere zichtbaar worden, ook
 * niet door een vergeten `where`. Daarom heeft **elke omgeving een eigen
 * IndexedDB-database**, `smartkeur-<tenantUuid>-<userUuid>`, naast de
 * apparaatbrede database `smartkeur` (sessie, apparaat-id, voorkeuren - zie
 * `db.ts`).
 *
 * Waarom geen tenant-kolom in één database:
 * - isolatie volgt uit de verbinding zelf; een query kan de rijen van een
 *   ander bedrijf niet eens zien;
 * - de sync-cursor, de outbox, de batches en de conflictenlijst horen van
 *   nature bij één omgeving: het token van `/sync/*` draagt één tenant;
 * - een omgeving opruimen is `Dexie.delete(naam)`, zonder migratie.
 *
 * Waarom ook de persoon in de naam: de outbox van een inspecteur mag nooit
 * met het token van een collega verstuurd worden die later op dezelfde tablet
 * inlogt. Het apparaat-id van de API is om dezelfde reden per persoon.
 *
 * Wisselen van omgeving sluit de ene verbinding en opent de andere
 * (`activateTenantDb`). Wat in de outbox van de vorige omgeving staat blijft
 * daar staan en gaat mee zodra die omgeving weer actief is - het token van de
 * nieuwe omgeving mag er niet bij.
 */
export const TENANT_DATABASE_PREFIX = 'smartkeur-';

export interface TenantScope {
    tenantUuid: string;
    userUuid: string | null;
}

export function tenantDatabaseName({ tenantUuid, userUuid }: TenantScope): string {
    return userUuid
        ? `${TENANT_DATABASE_PREFIX}${tenantUuid}-${userUuid}`
        : `${TENANT_DATABASE_PREFIX}${tenantUuid}`;
}

export class TenantDatabase extends Dexie {
    templateVersions!: Table<TemplateVersionRecord, string>;
    packageDocuments!: Table<PackageDocumentRecord, string>;
    orders!: Table<OrderRecord, string>;
    inspections!: Table<InspectionRecord, string>;
    answers!: Table<AnswerRecord, string>;
    multiCounts!: Table<MultiCountRecord, [string, string]>;
    findings!: Table<FindingRecord, string>;
    photos!: Table<PhotoRecord, string>;
    outbox!: Table<OutboxOperation, number>;
    batches!: Table<BatchRecord, string>;
    conflicts!: Table<ConflictRecord, string>;
    syncMeta!: Table<SyncMetaRecord, string>;

    readonly scope: TenantScope;

    constructor(scope: TenantScope, name: string = tenantDatabaseName(scope)) {
        super(name);
        this.scope = scope;

        /*
         * - `answers.[inspectionUuid+slot]` vindt het antwoord op (vraag, bereik);
         * - `outbox.entityKey` vindt een nog niet verstuurde bewerking op
         *   hetzelfde ding, zodat die vervangen wordt;
         * - `photos.[inspectionUuid+slot]` levert de foto's van één vraag.
         */
        this.version(1).stores({
            templateVersions: 'uuid',
            packageDocuments: 'uuid',
            orders: 'uuid, scheduledAt, inspectionUuid',
            inspections: 'uuid, orderUuid, templateVersionUuid',
            answers: 'answerUuid, inspectionUuid, &[inspectionUuid+slot]',
            multiCounts: '[inspectionUuid+slot], inspectionUuid',
            findings: 'findingUuid, inspectionUuid',
            photos: 'clientUuid, inspectionUuid, [inspectionUuid+slot], state',
            outbox: '++seq, &opUuid, status, entityKey, inspectionUuid, batchUuid',
            batches: 'batchUuid',
            conflicts: 'id, inspectionUuid, acknowledged, detectedAt',
            syncMeta: 'key',
        });
    }

    get tenantUuid(): string {
        return this.scope.tenantUuid;
    }
}

/* ------------------------------------------------------------------ */
/* De actieve omgeving                                                 */
/* ------------------------------------------------------------------ */

let active: TenantDatabase | null = null;
const listeners = new Set<() => void>();

/**
 * Maakt deze omgeving (van deze persoon) actief: sluit de vorige verbinding en
 * opent de database van deze omgeving. `null` sluit alles (uitgelogd).
 */
export function activateTenantDb(scope: TenantScope | null): TenantDatabase | null {
    const nextName = scope ? tenantDatabaseName(scope) : null;

    if ((active?.name ?? null) === nextName) {
        return active;
    }

    active?.close();
    active = scope ? new TenantDatabase(scope) : null;
    listeners.forEach((listener) => listener());

    return active;
}

/** De database van de actieve omgeving, of `null` zolang er geen omgeving gekozen is. */
export function getTenantDb(): TenantDatabase | null {
    return active;
}

export class NoActiveTenantError extends Error {
    constructor() {
        super('Er is geen omgeving actief op dit apparaat.');
        this.name = 'NoActiveTenantError';
    }
}

/** Voor schrijfacties: zonder omgeving is er niets om in te schrijven. */
export function requireTenantDb(): TenantDatabase {
    if (!active) {
        throw new NoActiveTenantError();
    }

    return active;
}

export function subscribeToTenantDb(listener: () => void): () => void {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
}

/** De database van de actieve omgeving; rendert opnieuw bij een wissel. */
export function useTenantDb(): TenantDatabase | null {
    return useSyncExternalStore(subscribeToTenantDb, getTenantDb, getTenantDb);
}

/** Alleen voor tests: een geïsoleerde database met een eigen naam. */
export function createTestTenantDb(
    tenantUuid = `tenant-${Math.random().toString(36).slice(2)}`,
): TenantDatabase {
    return new TenantDatabase(
        { tenantUuid, userUuid: null },
        `smartkeur-test-${tenantUuid}-${Math.random().toString(36).slice(2)}`,
    );
}
